import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import FAQCategorySection from "@/components/faq/FAQCategorySection";
import { FAQ_CATEGORIES, ALL_FAQ_ITEMS } from "@/lib/complianceFaqData";
import { Button } from "@/components/ui/button";

export default function ComplianceFAQPage() {
  return (
    <>
      <SEO
        title="Governance & Trust FAQ | LabComplyx"
        description="Evidence-based answers on regulatory accuracy, AI validation, audit readiness, data integrity, and security — for QA leads and compliance managers evaluating governed documentation workflows."
        keywords="AI validation GxP, regulatory traceability, 21 CFR Part 11 audit trail, CLIA compliance FAQ, AI governance laboratory, human in the loop review"
        canonical="https://labcomplyx.com/governance-faq"
        ogUrl="https://labcomplyx.com/governance-faq"
        pageType="faq"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Governance & Trust FAQ", url: "https://labcomplyx.com/governance-faq" },
        ]}
      />
      <FAQSchema faqItems={ALL_FAQ_ITEMS} />

      {/* Hero */}
      <div className="bg-brand-navy pt-28 lg:pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[13px] font-bold text-brand-teallight uppercase tracking-[0.12em]">
            Governance &amp; Trust FAQ
          </span>
          <h1 className="mt-4 text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            Can you trust this system with your lab's compliance program?
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto leading-relaxed">
            That's the real question every QA lead and compliance manager is asking. Here are
            evidence-based answers — regulatory traceability, AI validation, audit trails, data
            integrity, and security — stated plainly, including what we don't claim.
          </p>
        </div>
      </div>

      {/* Category nav */}
      <div className="bg-background border-b border-border sticky top-16 lg:top-20 z-40">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto">
          {FAQ_CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap bg-muted text-brand-navy hover:bg-accent transition-colors"
            >
              {c.title}
            </a>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 space-y-16">
        {FAQ_CATEGORIES.map((category) => (
          <FAQCategorySection key={category.id} category={category} />
        ))}
      </div>

      {/* CTA */}
      <div className="bg-muted/40 border-t border-border py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-brand-navy tracking-tight">
            Still have a question we didn't answer?
          </h2>
          <p className="mt-3 text-muted-foreground">
            Walk through it live — see the traceability, review gates, and audit trail on real workflows.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/demo">
              <Button className="bg-brand-teal hover:bg-brand-teal/90 text-white font-semibold w-full sm:w-auto">
                Watch the Live Demo
              </Button>
            </Link>
            <Link to="/portal-preview">
              <Button variant="outline" className="w-full sm:w-auto">
                Explore the Client Portal
              </Button>
            </Link>
            <Link to="/validation-intake">
              <Button variant="outline" className="w-full sm:w-auto">
                Book a Free Compliance Review
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}